import { ReactNode } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { Box, ButtonBase, Typography, useTheme } from "@mui/material";

import useGatedNavigate from "./useGatedNavigate";

// ----------------------------------------------------------------------

export type SidebarBadge = "IA" | "NOVO";

export interface SidebarNavItem {
  title: string;
  path: string;
  icon: ReactNode;
  badge?: SidebarBadge;
  /** Quando true, só ativa se o pathname for exato (end: true no matchPath) */
  exact?: boolean;
}

export interface SidebarNavGroup {
  label: string;
  items: SidebarNavItem[];
}

interface SidebarNavProps {
  groups: SidebarNavGroup[];
}

function NavBadge({ badge }: { badge: SidebarBadge }) {
  const theme = useTheme();
  const isIa = badge === "IA";
  return (
    <Box
      component="span"
      sx={{
        ml: "auto",
        px: isIa ? "7px" : "8px",
        py: "2px",
        borderRadius: "100px",
        fontSize: isIa ? "9.5px" : "9px",
        fontWeight: isIa ? 600 : 700,
        letterSpacing: isIa ? "1px" : "0.5px",
        backgroundColor: isIa ? theme.palette.brand.badgeIaBg : theme.palette.brand.cream,
        color: isIa ? theme.palette.brand.badgeIaText : theme.palette.primary.dark,
      }}
    >
      {badge}
    </Box>
  );
}

/** Navegação da sidebar: grupos com label, itens com badge e gating de plano */
export default function SidebarNav({ groups }: SidebarNavProps) {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { navigateGated, gateDialogs } = useGatedNavigate();

  const isActive = (item: SidebarNavItem) =>
    !!matchPath({ path: item.path, end: item.exact ?? false }, pathname);

  return (
    <Box sx={{ px: 2 }}>
      {gateDialogs}

      {groups.map(({ label, items }, groupIndex) => (
        <Box key={label} sx={{ mt: groupIndex === 0 ? 0 : "22px" }}>
          <Typography
            sx={{
              m: "8px 8px 10px",
              fontSize: "10.5px",
              fontWeight: 600,
              letterSpacing: "1.6px",
              textTransform: "uppercase",
              color: theme.palette.brand.navGroupLabel,
            }}
          >
            {label}
          </Typography>

          {items.map((item) => {
            const active = isActive(item);
            return (
              <ButtonBase
                key={item.path}
                onClick={() => navigateGated(item.path)}
                sx={{
                  width: "100%",
                  justifyContent: "flex-start",
                  gap: "13px",
                  p: "11px 13px",
                  mb: "3px",
                  borderRadius: "12px",
                  fontFamily: theme.typography.fontFamily,
                  fontSize: "14px",
                  fontWeight: active ? 600 : 500,
                  color: active ? theme.palette.brand.navActiveText : theme.palette.brand.navItemText,
                  backgroundColor: active ? theme.palette.brand.navActiveBg : "transparent",
                  transition: "background .15s",
                  "&:hover": {
                    backgroundColor: active
                      ? theme.palette.brand.navActiveBg
                      : theme.palette.brand.navHoverBg,
                  },
                }}
              >
                <Box component="span" sx={{ display: "inline-flex", "& svg": { width: 19, height: 19 } }}>
                  {item.icon}
                </Box>
                <Box component="span" sx={{ flex: 1, textAlign: "left" }}>
                  {item.title}
                </Box>
                {item.badge && <NavBadge badge={item.badge} />}
              </ButtonBase>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}
